import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Users, Plus, Search, Shield, Edit, Trash2, UserCheck, UserX } from 'lucide-react';
import api from '../lib/api';
import { formatDate } from '../lib/utils';

const StaffUsers = () => {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'MODERATOR'
  });

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      
      const response = await api.get(`/admin/staff?${params.toString()}`);
      setStaff(response.data.items || []);
    } catch (error) {
      toast.error('Gabim në marrjen e përdoruesve');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [roleFilter]);

  const handleCreate = async () => {
    try {
      if (!formData.email || !formData.password || !formData.full_name) {
        toast.error('Plotëso të gjitha fushat e detyrueshme');
        return;
      }
      
      await api.post('/admin/staff', formData);
      toast.success('Përdoruesi u krijua me sukses');
      setCreateDialogOpen(false);
      setFormData({ email: '', password: '', full_name: '', phone: '', role: 'MODERATOR' });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në krijimin e përdoruesit');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/admin/staff/${selectedStaff.id}`, {
        full_name: formData.full_name,
        phone: formData.phone,
        role: formData.role,
        is_active: formData.is_active
      });
      toast.success('Përdoruesi u përditësua');
      setEditDialogOpen(false);
      setSelectedStaff(null);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në përditësimin e përdoruesit');
    }
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('A jeni të sigurt që dëshironi ta çaktivizoni këtë përdorues?')) return;
    
    try {
      await api.delete(`/admin/staff/${staffId}`);
      toast.success('Përdoruesi u çaktivizua');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  const openEditDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      email: staffMember.email,
      full_name: staffMember.full_name,
      phone: staffMember.phone || '',
      role: staffMember.role,
      is_active: staffMember.is_active
    });
    setEditDialogOpen(true);
  };

  const filteredStaff = staff.filter(s => 
    s.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const colors = {
      'SUPER_ADMIN': 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      'ADMIN': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      'MODERATOR': 'bg-green-500/10 text-green-500 border-green-500/20',
      'DISPATCHER': 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      'BUSINESS': 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
    };
    const labels = {
      'SUPER_ADMIN': 'Super Admin',
      'ADMIN': 'Administrator',
      'MODERATOR': 'Moderator',
      'DISPATCHER': 'Dispançer',
      'BUSINESS': 'Biznes'
    };
    return (
      <Badge variant="outline" className={colors[role] || 'bg-muted'}>
        {labels[role] || role}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="staff-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-outfit flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Përdoruesit e Sistemit
          </h1>
          <p className="text-muted-foreground mt-1">
            Menaxho përdoruesit që kanë qasje në panelin administrativ
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-staff-btn">
          <Plus className="w-4 h-4 mr-2" />
          Shto Përdorues
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Kërko me emër ose email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="staff-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]" data-testid="staff-role-filter">
                <SelectValue placeholder="Roli" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të gjithë</SelectItem>
                <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                <SelectItem value="ADMIN">Administrator</SelectItem>
                <SelectItem value="MODERATOR">Moderator</SelectItem>
                <SelectItem value="BUSINESS">Biznes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit">Lista e Përdoruesve</CardTitle>
          <CardDescription>
            {filteredStaff.length} përdorues gjithsej
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Emri</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Roli</TableHead>
                  <TableHead>Statusi</TableHead>
                  <TableHead>Krijuar</TableHead>
                  <TableHead className="text-right">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStaff.map((member) => (
                  <TableRow key={member.id} data-testid={`staff-row-${member.id}`}>
                    <TableCell className="font-medium">{member.full_name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{getRoleBadge(member.role)}</TableCell>
                    <TableCell>
                      {member.is_active ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                          <UserCheck className="w-3 h-3 mr-1" /> Aktiv
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
                          <UserX className="w-3 h-3 mr-1" /> Joaktiv
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(member.created_at, { includeTime: false })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(member)}
                          data-testid={`edit-staff-${member.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {member.id !== user?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(member.id)}
                            data-testid={`delete-staff-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredStaff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Nuk u gjetën përdorues
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Shto Përdorues të Ri
            </DialogTitle>
            <DialogDescription>
              Krijo një llogari të re për akses në panelin administrativ
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Emri i Plotë *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Emri Mbiemri"
                data-testid="staff-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="staff-form-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Fjalëkalimi *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                data-testid="staff-form-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefoni</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+355 6X XXX XXXX"
                data-testid="staff-form-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Roli *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="staff-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MODERATOR">Moderator</SelectItem>
                  <SelectItem value="ADMIN">Administrator</SelectItem>
                  {user?.role === 'SUPER_ADMIN' && (
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Anulo
            </Button>
            <Button onClick={handleCreate} data-testid="staff-form-submit">
              Krijo Përdoruesin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Modifiko Përdoruesin</DialogTitle>
            <DialogDescription>
              Përditëso informacionet e përdoruesit
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Emri i Plotë</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={formData.email} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>Telefoni</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Roli</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MODERATOR">Moderator</SelectItem>
                  <SelectItem value="ADMIN">Administrator</SelectItem>
                  {user?.role === 'SUPER_ADMIN' && (
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Anulo
            </Button>
            <Button onClick={handleUpdate}>
              Ruaj Ndryshimet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffUsers;
